import { PdfDocumentProxy } from '../types';
import { loadPdfJs } from './pdfjsLoader';

const ensurePromiseTry = () => {
  const promiseCtor = Promise as PromiseConstructor & {
    try?: (fn: (...args: unknown[]) => unknown, ...args: unknown[]) => Promise<unknown>;
  };

  if (typeof promiseCtor.try === 'function') return;

  promiseCtor.try = (fn: (...args: unknown[]) => unknown, ...args: unknown[]) =>
    new Promise((resolve, reject) => {
      try {
        resolve(fn(...args));
      } catch (error) {
        reject(error);
      }
    });
};

const isSafariBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Safari/i.test(ua) && !/(Chrome|Chromium|CriOS|Edg|OPR|FxiOS|Firefox|Android)/i.test(ua);
};

/**
 * Load a PDF document from a URL
 * @param fileUrl - URL to the PDF file (can be remote URL or blob URL)
 * @returns Promise with PDF document proxy
 */
export const loadPdfDocument = async (fileUrl: string): Promise<PdfDocumentProxy> => {
  ensurePromiseTry();
  const pdfjsLib = await loadPdfJs();
  const disableWorker = isSafariBrowser();
  const loadingTask = pdfjsLib.getDocument({
    url: fileUrl,
    disableFontFace: false,
    useSystemFonts: false,
    // Safari can fail inside the worker runtime on unsupported Promise helpers.
    disableWorker,
  });
  return loadingTask.promise;
};

/**
 * Load a PDF document from a File object
 * @param file - The PDF file to load
 * @returns Promise with PDF document proxy
 */
export const loadPdfFromFile = async (file: File): Promise<PdfDocumentProxy> => {
  ensurePromiseTry();
  const pdfjsLib = await loadPdfJs();
  const arrayBuffer = await file.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  const loadingTask = pdfjsLib.getDocument({
    data: uint8Array as any,
    disableWorker: isSafariBrowser(),
  });
  return loadingTask.promise;
};

/**
 * Create a temporary blob URL from a File object for preview
 * Remember to revoke the URL when done: URL.revokeObjectURL(url)
 * @param file - The PDF file
 * @returns Blob URL
 */
export const createPdfBlobUrl = (file: File): string => {
  return URL.createObjectURL(file);
};
